import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { GUIDE_CARD_TITLE_CLAMP_CSS } from '../guide/guideCardWidth';
import { GUIDE_BROWSE_TILE_COUNT } from '../guide/publishedGuides';

/**
 * The guide card shared by "New guides" and "More gift guides" (THE-822 /
 * WS5, canvas p03 tiles 03-04; PRD p.10 "Guide preview card").
 *
 * A HUB-LOCAL FORK, same posture as `HubGuideGrid` and `GuidePeekCard` — see
 * `GiftGuideHubView`'s file header for why hub restyles get their own
 * components instead of bending the ones `/gift-guides`, the homepage rail
 * and the board rail also render. This card's white-card-with-shadow
 * treatment and bottom-anchored title are new to this row; neither
 * `GuidePeekCard` (no shadow, title can sit above) nor `HubGuideGrid` (one
 * cover photo, not three) draw it.
 *
 * ONE CARD, TWO ROWS. "New guides" and "More gift guides" differ in how many
 * columns/cells of this card they lay out and whether `badge` is set — not in
 * what the card itself looks like (PRD p.10's row says "same collage shape,
 * no badge" for More gift guides). Forking the card a second time per row
 * would let the two drift exactly the way `HubPickChip`'s header warns
 * against.
 */

const Card = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
  background: #ffffff;
  border-radius: ${({ theme }) => theme.radius['2xl']};
  /* PRD p.10: "soft drop shadow (0 1px 2px and 0 6px 18px, both near-black at
     6-8% opacity) so the photos and title read as one tappable unit." */
  box-shadow:
    0 1px 2px rgba(42, 33, 28, 0.07),
    0 6px 18px rgba(42, 33, 28, 0.07);
  overflow: hidden;
`;

const Strip = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${GUIDE_BROWSE_TILE_COUNT}, 1fr);
  gap: 2px;
  background: ${({ theme }) => theme.color.cream};
`;

const Tile = styled.div`
  aspect-ratio: 2 / 3;
  overflow: hidden;
`;

const TileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/** PRD p.10 said a white pill, text #38322E — but the white pill vanished on
 *  light collage photos. Kate (2026-10-07): near-black #2A211C pill, white
 *  text, her standing "black/white pills, not clay" rule. */
const NewPill = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  border-radius: 9999px;
  background: #2a211c;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1;
  padding: 5px 10px;
`;

const TitleRow = styled.div`
  padding: 12px 14px 14px;
`;

/**
 * PRD p.10: "Guide card title + emoji: 16px phones, 18px desktop; bold,
 * text #38322E." THE-859 (board override of the canvas): "New guides"'
 * bigger card takes a 20px desktop title via `$large` — "More gift guides"
 * reuses this same card unchanged, at 18px.
 *
 * `$fit` is ALSO the board's "Dive deeper" band flag (see the `fitTitle`
 * doc below — only GuidePeekRail's `compact` rail passes it, never the hub
 * rows `$large` serves). THE-1232: on the board that PRD-pinned 16/18/700
 * ink literal is the standard's "no fifth size"/"nothing bold-bold"
 * violation Kate measured — `$fit` swaps it to `theme.text.tile`
 * (15/17, the same "tile titles" role the band's own heading reads) +
 * `theme.weight.semibold` + `theme.color.foreground`. The hub rows
 * (`$fit` unset) keep the PRD's literal 16/18/700/#38322e exactly as
 * specified — that spec is this card's own, not the board standard's, and
 * is out of this ticket's scope.
 */
const Title = styled.span<{ $large?: boolean; $fit?: boolean }>`
  line-height: 1.25;
  min-width: 0;
  font-size: ${({ $fit, theme }) => ($fit ? theme.text.tile.mobile : '16px')};
  font-weight: ${({ $fit, theme }) => ($fit ? theme.weight.semibold : 700)};
  letter-spacing: -0.015em;
  color: ${({ $fit, theme }) => ($fit ? theme.color.foreground : '#38322e')};
  text-wrap: balance;
  /* THE-876 rule 4: reserve exactly 2 lines, same mechanism as GuidePeekCard's
     Title, so "New guides"/"More gift guides" cards line up with every other
     standard-width row. */
  ${GUIDE_CARD_TITLE_CLAMP_CSS}
  min-height: ${({ $fit }) => ($fit ? '0' : 'calc(2 * 1.25em)')};
  max-height: calc(2 * 1.25em);
  @media (min-width: 768px) {
    font-size: ${({ $fit, $large, theme }) =>
      $fit ? theme.text.tile.desktop : $large ? '20px' : '18px'};
  }
`;

const Emoji = styled.span`
  font-size: 0.9em;
`;

/* THE-894: the emoji used to sit beside Title as a flex sibling
   (`display: flex` on TitleRow) — mobile WebKit does not shrink a
   `-webkit-line-clamp` flex child reliably (Title carries
   GUIDE_CARD_TITLE_CLAMP_CSS's `display: -webkit-box`), so Title kept its
   full single-line intrinsic width instead of shrinking to its wrapped
   two-line content, and Emoji landed at the far right of that oversized,
   invisible box instead of right after the visible text.
   GuidePeekCard's title already solves this the same way: the emoji is
   TEXT, pinned to the title's last word with a non-breaking space inside a
   `white-space: nowrap` span, so it travels with the word it follows
   through ordinary text flow/wrap rather than through a sibling layout box
   that can detach from it. */
const PinnedTail = styled.span`
  white-space: nowrap;
`;

const NBSP = ' ';

export interface HubGuideCollageCardProps {
  href: string;
  title: string;
  emoji: string;
  /** Peek image urls; only the first GUIDE_BROWSE_TILE_COUNT render. Short or
   *  empty strips pad with neutral placeholder tiles, same as GuidePeekCard. */
  imageUrls: string[];
  /** The white "NEW" pill, top-left of the collage. Omitted on "More gift
   *  guides" (PRD p.10: "same collage shape, no badge"). */
  badge?: boolean;
  /** THE-859: a 20px desktop title instead of 18px, for "New guides"' bigger
   *  card. Omitted (and unchanged) on "More gift guides". */
  large?: boolean;
  /** Don't reserve the second title line: the card ends right after its
   *  title (still clamped to 2 lines). For a row that equalizes card heights
   *  itself — the board's "Dive deeper" band (GuidePeekRail `compact`), where
   *  the reserved line read as dead space under every one-line title (Kate
   *  2026-10-14). Omitted on the hub rows, which line up across rows by it. */
  fitTitle?: boolean;
  /** Passed the native click event, not just notified — mirrors GuidePeekCard
   *  (THE-950's board rail reads modifier keys off it to tell a plain click
   *  from a ⌘/Ctrl/middle-click). Every other caller ignores the argument. */
  onClick?: (event: React.MouseEvent) => void;
  /** react-router link state — the hub's `fromHub` back-link marker. */
  linkState?: unknown;
  className?: string;
}

export const HubGuideCollageCard: React.FC<HubGuideCollageCardProps> = ({
  href,
  title,
  emoji,
  imageUrls,
  badge,
  large,
  fitTitle,
  onClick,
  linkState,
  className,
}) => {
  // Split the title so only its FINAL word is bound to the emoji; everything
  // before it wraps normally. Same technique as GuidePeekCard's title.
  const lastSpace = title.lastIndexOf(' ');
  const head = lastSpace === -1 ? '' : title.slice(0, lastSpace + 1);
  const lastWord = lastSpace === -1 ? title : title.slice(lastSpace + 1);

  return (
    <Card to={href} state={linkState} onClick={onClick} aria-label={title} className={className}>
      <Strip>
        {Array.from({ length: GUIDE_BROWSE_TILE_COUNT }, (_, i) => {
          const url = imageUrls[i];
          return (
            <Tile key={i} data-testid={url ? 'hub-guide-collage-tile' : 'hub-guide-collage-placeholder'}>
              {url ? <TileImage src={url} alt="" loading="lazy" /> : null}
            </Tile>
          );
        })}
        {badge && <NewPill data-testid="hub-guide-new-badge">New</NewPill>}
      </Strip>
      <TitleRow>
        <Title $large={large} $fit={fitTitle} data-guide-title>
          {head}
          <PinnedTail>
            {lastWord}
            {NBSP}
            <Emoji aria-hidden="true">{emoji}</Emoji>
          </PinnedTail>
        </Title>
      </TitleRow>
    </Card>
  );
};

export default HubGuideCollageCard;
